import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { type MaybeRefOrGetter, toValue } from "vue";
import { request } from "@/shared/http/client";
import type {
	BillingMe,
	ExtraRow,
	ExtrasPatch,
	PackPurchase,
	PackSku,
	Plan,
	PlanChangePreview,
	PlanChoiceResult,
} from "./billing.types";
import { announceAuthenticated, finishAuthentication, needsAuthentication } from "./pack-3ds";

/**
 * What this workspace's contract looks like, and the four ways to change it.
 *
 * `GET /v3/billing/me` is the ONE payload the six customer screens share, and it
 * is asked for once: plan, subscription, credits, limits and the pending invoice
 * arrive together because they are read together on every one of those screens.
 * Six narrower endpoints would mean six moments where the plan on the header
 * disagrees with the plan on the grid below it.
 *
 * There is no plan guard anywhere in this app. `plan: null` is a legitimate
 * answer that every screen must survive — the wall is `/escolher-plano`, offered,
 * and the only real gate is the one in front of invoking the AI.
 */
export function useBillingMe(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: ["billing", "me"] as const,
		queryFn: () => request<BillingMe>("/v3/billing/me"),
		enabled: () => toValue(enabled),
		staleTime: 30_000,
	});
}

/** The commercial catalogue, as the customer sees it: active plans, in order. */
export function useBillingPlans(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: ["billing", "plans"] as const,
		queryFn: async () => {
			const res = await request<{ plans: Plan[] }>("/v3/billing/plans");
			return res.plans;
		},
		enabled: () => toValue(enabled),
		staleTime: 300_000,
	});
}

export function useBillingPacks(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: ["billing", "packs"] as const,
		queryFn: async () => {
			const res = await request<{ packs: PackSku[] }>("/v3/billing/packs");
			return res.packs;
		},
		enabled: () => toValue(enabled),
		staleTime: 300_000,
	});
}

/**
 * The extras, with the two facts `/billing/me` does not carry: what was WAIVED
 * (granted, not invoiced) and what is SCHEDULED to shrink at the cycle's end.
 */
export function useBillingExtras(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: ["billing", "extras"] as const,
		queryFn: async () => {
			const res = await request<{ extras: ExtraRow[] }>("/v3/billing/extras");
			return res.extras;
		},
		enabled: () => toValue(enabled),
	});
}

/**
 * Choosing a plan.
 *
 * The answer may carry a `checkoutUrl`, and only ever on the gateway rail. A
 * paid plan with the gateway off is REFUSED (`GATEWAY_UNAVAILABLE`, 503) —
 * desde 28/09/2026 não há trilho manual onde a fatura seria paga por fora. Um
 * plano gratuito é gravado na hora, sem cartão nenhum.
 */
/**
 * A prévia pedida uma vez, no gesto — a saída do teste pergunta "o que passa a
 * ser adicional?" no clique de "Começar por aqui", não enquanto a grade está
 * aberta. O mesmo `GET plan/preview/:code` que o diálogo de troca lê.
 */
export function fetchPlanPreview(planCode: string): Promise<PlanChangePreview> {
	return request<PlanChangePreview>(`/v3/billing/plan/preview/${planCode}`);
}

/** Mensal ou anual — escolhido na entrada; uma assinatura que corre fica no dela (0257). */
export type BillingInterval = "month" | "year";

export function useChoosePlan() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { planCode: string; interval?: BillingInterval }) =>
			request<PlanChoiceResult>("/v3/billing/plan", { method: "POST", body: input }),
		onSuccess: () => invalidateBilling(client),
	});
}

/**
 * O QUE ACONTECE SE EU TROCAR — sem que nada aconteça.
 *
 * `enabled` pelo código: o diálogo só pergunta depois de a pessoa escolher um
 * alvo, e uma prévia sem alvo seria uma chamada por render. `staleTime` zero de
 * propósito — o que ela responde depende do uso de HOJE (usuários, contatos), e
 * uma prévia em cache é a que diz que cabe quando já não cabe.
 */
export function usePlanChangePreview(
	planCode: MaybeRefOrGetter<string>,
	enabled: MaybeRefOrGetter<boolean> = true,
) {
	return useQuery({
		queryKey: ["billing", "plan-preview", () => toValue(planCode)] as const,
		queryFn: () => request<PlanChangePreview>(`/v3/billing/plan/preview/${toValue(planCode)}`),
		enabled: () => toValue(enabled) && toValue(planCode) !== "",
		staleTime: 0,
	});
}

/**
 * TROCAR DE PLANO. Distinta de `useChoosePlan`, e a diferença não é cosmética:
 * aquela é a ENTRADA (pode devolver um `checkoutUrl` e mandar a pessoa à
 * Stripe), esta é a MUDANÇA de quem já paga — nunca abre checkout, cobra o
 * proporcional numa subida e agenda numa descida.
 */
export function useChangePlan() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (planCode: string) =>
			request<PlanChangePreview & { applied: boolean }>("/v3/billing/plan/change", {
				method: "POST",
				body: { planCode },
			}),
		onSuccess: () => invalidateBilling(client),
	});
}

export function useCancelSubscription() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: () => request<unknown>("/v3/billing/subscription", { method: "DELETE" }),
		onSuccess: () => invalidateBilling(client),
	});
}

export function useReactivateSubscription() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: () => request<unknown>("/v3/billing/subscription/reactivate", { method: "POST" }),
		onSuccess: () => invalidateBilling(client),
	});
}

export function useBuyPack() {
	const client = useQueryClient();
	return useMutation({
		/**
		 * COMPRAR UM PACOTE — incluindo o 3-D Secure, quando o banco o pede.
		 *
		 * A autenticação vive AQUI e não no componente por uma razão prática: as
		 * telas que vendem um pacote não deviam ter de saber o que um
		 * `client_secret` é. Para elas a compra continua a ser uma promessa que
		 * resolve com a compra feita, ou atira uma recusa que `describeServerError`
		 * traduz — exatamente o contrato de antes de o 3-D Secure existir.
		 *
		 * O que muda é o meio: um 402 `CHARGE_REQUIRES_ACTION` deixa de ser o fim e
		 * passa a ser um desvio. Sem chave publicável, ou com a autenticação
		 * falhada, a recusa original sobe tal como estava.
		 */
		mutationFn: async (code: string) => {
			try {
				return await request<PackPurchase>(`/v3/billing/packs/${encodeURIComponent(code)}/buy`, {
					method: "POST",
				});
			} catch (e) {
				if (!needsAuthentication(e)) throw e;
				const bought = await finishAuthentication({
					refusal: e,
					clientSecret: e.payload.clientSecret ?? "",
					purchaseId: e.payload.purchaseId ?? null,
					paymentMethodRef: e.payload.paymentMethodRef ?? null,
				});
				announceAuthenticated();
				return bought;
			}
		},
		onSuccess: () => invalidateBilling(client),
	});
}

/**
 * Buying or giving back extra capacity — ONE patch with every resource that
 * changed. The pro rata is asymmetric (up charges now, down waits for the
 * cycle's end) and only the server can compute it; one request per resource
 * would price a two-resource change twice.
 */
export function useSaveExtras() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (patch: ExtrasPatch) =>
			request<unknown>("/v3/billing/extras", { method: "PATCH", body: patch }),
		onSuccess: () => invalidateBilling(client),
	});
}

/**
 * One prefix, not a list of them. Every write here moves at least three readers
 * — the contract, the wallet and the invoice it will land on — and naming each
 * is three places to forget the fourth.
 */
export function invalidateBilling(client: ReturnType<typeof useQueryClient>): void {
	void client.invalidateQueries({ queryKey: ["billing"] });
	void client.invalidateQueries({ queryKey: ["credits"] });
}
